import React, { useEffect } from 'react';
import { AppAuthRoot } from '@n20a/libauth';
import { User } from 'firebase/auth';
import { auth, TENANT_CONFIG } from './utils/firebase-config';
import { MainApp } from './MainApp';

// import '@n20a/libauth/scoped.css' // Removed - using standard CSS

/**
 * Root component - uses AppAuthRoot from @n20a/libauth for authentication
 * Tenant is pre-configured in firebase-config.ts via initializeFirebaseAuth()
 * This component only handles tenant validation and multi-tab sync
 */
export const AppRoot: React.FC = () => {
  // Clean all auth state on mount (when login form is shown)
  // This ensures a fresh start every time, preventing stale state issues
  useEffect(() => {
    console.log('⏱️ AppRoot mounted at:', Date.now());
    console.log('Cleaning auth state on mount...');
    
    // Tenant ID is already configured in firebase-config.ts via initializeFirebaseAuth()
    // DO NOT reset it to null here, as it breaks authentication
    
    // Note: We don't clear localStorage here because Firebase needs it
    // to check if user is already authenticated. Cleanup happens on logout.
    
    console.log('Tenant configured:', TENANT_CONFIG.NETZOOM);
    console.log('Current tenant ID:', auth.tenantId);
  }, []); // Run once on mount
  
  // Setup multi-tab sync listener
  useEffect(() => {

    // Listen for storage events to handle auth changes across tabs
    const handleStorageChange = (e: StorageEvent) => {
      // If auth user is removed in another tab, reload this tab
      if (e.key && e.key.startsWith('firebase:authUser')) {
        if (e.newValue === null) {
          console.log('Auth cleared in another tab, reloading...');
          window.location.reload();
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleAuthenticated = (user: User): React.ReactNode => {
    console.log('User authenticated:', user);
    
    // Note: Tenant validation removed - Firebase handles this internally
    // The tenant ID is configured in firebase-config.ts and enforced by Firebase
    
    // Clear any stale clear attempt marker
    sessionStorage.removeItem('tenant-clear-attempt');
    
    // Use user.uid as key to force complete remount when user changes
    // This ensures all component state is fresh for each login
    return <MainApp key={user.uid} user={user} />;
  };

  return (
    <AppAuthRoot
      auth={auth}
      authType="fb"
      onAuthenticated={handleAuthenticated}
    />
  );
};
